import { I18n } from '@boschebike/ebds-js';
import { IconAsChunk } from '@boschebike/ebds-react';
import { useContext, useEffect } from 'react';
import {
  Container, InternalEventLink, InternalNavigationLink, Page,
} from '../../components';
import envReader from '../../config/envReader.ts';
import { termsOfUseRoutes } from '../../config/router';
import { TERMS_OF_USE_TABS } from '../../config/routerLocations';
import { DxfIntegrationContext } from '../../context/dxfIntegrationContext';
import { PlatformContext } from '../../context/platformContext.tsx';
import { RoutingContext } from '../../context/routingContext';
import { onScroll } from '../../helpers/scroll';
import { interpolateContentToJSX } from '../../helpers/utils';
import { useContent } from '../../hooks/use-content';

const docPrint = (): void => {
  window.print();
};

const docMap = [
  { name: 'Czech Republic', link: 'DPA_IBD_CZ_Update_Aug_25.pdf' },
  { name: 'Poland', link: 'DPA_IBD_PL_Update_Aug_25.pdf' },
  { name: 'Portugal', link: 'DPA_IBD_PT_Update_Aug_25.pdf' },
];

export const DataProcessingAgreementPage = () => {
  const platformContext = useContext(PlatformContext);
  const dxfIntegrationContext = useContext(DxfIntegrationContext);
  const { isLoaded, replaceTab } = useContext(RoutingContext);
  const content: I18n = useContent();

  useEffect(() => {
    if (dxfIntegrationContext.isLoaded && !dxfIntegrationContext.isDPASupported) {
      replaceTab(TERMS_OF_USE_TABS.TERMS_OF_USE);
    }
  }, [isLoaded, dxfIntegrationContext.isLoaded, dxfIntegrationContext.isDPASupported, replaceTab]);

  const contentKeyPage: string = content.t('portal.data.processingAgreement');
  const contentKeyAnnex: string = content.t('portal.data.processingAgreement.annex');

  const jsxContent: JSX.Element[] = interpolateContentToJSX(contentKeyPage, [
    {
      selector: 'annex1-scroller',
      element: InternalEventLink,
      elementProps: {
        onClickEvent: onScroll(contentKeyPage, 'section-annex-head-1'),
        text: `${contentKeyAnnex} 1`,
      },
    },
    {
      selector: 'annex2-scroller',
      element: InternalEventLink,
      elementProps: {
        onClickEvent: onScroll(contentKeyPage, 'section-annex-head-2'),
        text: `${contentKeyAnnex} 2`,
      },
    },
  ]);

  return (
    <Page
      links={termsOfUseRoutes(content, { isDPASupported: dxfIntegrationContext.isDPASupported, isDXF: platformContext.isDXF })}
      enforcedTabId="500"
    >
      <div className="print-button-wrapper" style={{ display: 'none' }}>
        <button
          type='button'
          className="ebds-bosch-icon ebds-bosch-icon--none print-button"
          onClick={docPrint}
          aria-label="print"
        >
          <IconAsChunk iconName="print" />
        </button>
      </div>
      {dxfIntegrationContext.isDPASupported && (
        <>
          <div className="dpa-note">
            <h2>Note: This Data Processing Agreement only applies to Bosch eBike dealers located in Europe.</h2>
          </div>

          <Container>
            <ul className="dpa-list">
              {docMap.map((d) => (
                <li key={d.name}>
                  <InternalNavigationLink
                    href={`${envReader.VITE_PUBLIC_URL}/dpa/${d.link}`}
                    text={d.name}
                  />
                </li>
              ))}
            </ul>
          </Container>
        </>
      )}
      {...jsxContent}
    </Page>
  );
};
